import { ProtocolConsumption } from '../../../models/Entities/Protocols/ProtocolConsumption';
import { ProtocolConsumptionConditionEnum } from '../../../models/Enums/ProtocolConsumptionConditionEnum';
import { StatusTone } from '../../../theme/guideTokens';

/*
 * Formato y cálculos puros del detalle de consumo (sin React ni store).
 */

const ISO_CURRENCY = /^[A-Z]{3}$/;

/** Código ISO válido o ARS por defecto (Intl exige tres letras). */
export const resolveCurrencyCode = (currency: string | undefined | null): string => {
    const normalized = (currency ?? '').trim().toUpperCase();
    return ISO_CURRENCY.test(normalized) ? normalized : 'ARS';
};

export const fmtCurrencyCode = resolveCurrencyCode;

export const fmtCurrency = (amount: number | undefined | null, currency: string | undefined | null): string => {
    const safeCurrency = resolveCurrencyCode(currency);
    try {
        return (amount ?? 0).toLocaleString('es-AR', { style: 'currency', currency: safeCurrency });
    } catch {
        return (amount ?? 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
    }
};

/** Importe sin símbolo: la grilla dice la moneda una sola vez en el encabezado. */
export const fmtAmount = (amount: number | undefined | null): string =>
    (amount ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtNumber = (value: number | undefined | null, minimumFractionDigits = 2, maximumFractionDigits = 2): string => {
    if (value === undefined || value === null || Number.isNaN(value)) return '–';
    return value.toLocaleString('es-AR', { minimumFractionDigits, maximumFractionDigits });
};

export const fmtPercent = (value: number | undefined | null): string => `${fmtNumber(value, 0, 3)}%`;

/** Tono y símbolo de la etiqueta de estado de la guía para el estado del consumo. */
export const statusToneOf = (status: number | undefined): { tone: StatusTone; symbol: string } => {
    switch (status) {
        case 2:
        case 3:
            return { tone: 'success', symbol: '●' };
        case 4:
            return { tone: 'danger', symbol: '×' };
        case 5:
            return { tone: 'neutral', symbol: '○' };
        default:
            return { tone: 'warning', symbol: '!' };
    }
};

export interface SettlementTotals {
    ownerNet: number;
    teamNet: number;
    total: number;
}

/** Neto del centro y del equipo: principal (ya con retención) más adicionales. */
export const settlementTotalsOf = (c: ProtocolConsumption | null | undefined): SettlementTotals => {
    const ownerNet =
        (c?.ownerAmountWithholdingTotal ?? 0) +
        (c?.conceptValues?.reduce((acc, cv) => acc + (cv.applyWithholding ? cv.finalOwnerAmount : cv.ownerAmount), 0) ?? 0);
    const teamNet =
        (c?.teamAmountWithholdingTotal ?? 0) +
        (c?.conceptValues?.reduce((acc, cv) => acc + (cv.applyWithholding ? cv.finalTeamAmount : cv.teamAmount), 0) ?? 0);
    return { ownerNet, teamNet, total: ownerNet + teamNet };
};

export interface WithholdingTotals {
    main: number;
    additional: number;
    total: number;
}

export const withholdingTotalsOf = (c: ProtocolConsumption | null | undefined): WithholdingTotals => {
    const main = c?.withHolingAmount ?? 0;
    const additional = c?.conceptValues?.reduce((acc, cv) => acc + (cv.applyWithholding ? (cv.withholdingAmount ?? 0) : 0), 0) ?? 0;
    return { main, additional, total: main + additional };
};

/** Hay conversión de moneda: la original existe y es distinta de la del consumo. */
export const hasQuotation = (c: ProtocolConsumption | null | undefined): boolean =>
    !!c?.originalCurrencyCode && fmtCurrencyCode(c.currencyCode) !== fmtCurrencyCode(c.originalCurrencyCode);

/** Marcas del consumo, derivadas de donde el backend las deja (igual que en la grilla). */
export const marksOf = (c: ProtocolConsumption | null | undefined) => ({
    isProcedure: (c?.tag4?.length ?? 0) > 0,
    isAdditional: c?.typeOfService === 2,
    isEarlyDiscontinuation: c?.condition === ProtocolConsumptionConditionEnum.EarlyDiscontinuation,
    isFollowUp: c?.condition === ProtocolConsumptionConditionEnum.FollowUp || !!c?.markedAsFollowUp,
    isGroupedInVisit: !!c?.parentGroupingId
});

/** Nota secundaria del importe: cantidad y coeficiente, si vienen. */
export const amountNoteOf = (c: ProtocolConsumption): string | undefined => {
    const hasQuantity = c.quantity !== undefined && c.quantity !== null && !Number.isNaN(c.quantity);
    const hasCoefficient = c.coefficientApplied !== undefined && c.coefficientApplied !== null && !Number.isNaN(c.coefficientApplied);
    return [
        hasQuantity ? `Cant. ${fmtNumber(c.quantity, 0, 3)}` : undefined,
        hasCoefficient ? `Coef. ${fmtNumber(c.coefficientApplied, 2, 3)}` : undefined
    ].filter(Boolean).join(' · ') || undefined;
};
